import booleanClockwise from '@turf/boolean-clockwise';
import { lineString, polygon as turfPolygon } from '@turf/helpers';
import { rewind } from '@turf/rewind';
import turfUnion from '@turf/union';

const cornerConfig = {
    ceilZone: 'corner',
    fillColor: '#F00',
    strokeColor: '#F00',
};
const edgeConfig = {
    ceilZone: 'edge',
    fillColor: '#FF0',
    strokeColor: '#FF0',
};
const PITCHED_ROOF_TILT_THRESHOLD = 27;

export function buildZone({ google, heading, height, start, width } = {}) {
    const parsedHeading = parseFloat(heading);

    const pointA = computeOffsetPoint({
        distance: width,
        google,
        heading: parsedHeading - 180,
        start,
    });
    const pointB = computeOffsetPoint({
        distance: height,
        google,
        heading: parsedHeading - 90,
        start: pointA,
    });
    const pointC = computeOffsetPoint({
        distance: width,
        google,
        heading: parsedHeading,
        start: pointB,
    });
    return [start, pointA, pointB, pointC];
}

export function buildMonoPitchedRoofAreas({
    buildingHeight,
    google,
    heading,
    polygonPoints,
} = {}) {
    if (!buildingHeight) return [];

    const a = 0.4 * buildingHeight;
    const twoA = 0.8 * buildingHeight;
    const fourA = 1.6 * buildingHeight;
    const parsedHeading = parseFloat(heading);

    const [point1, point2, point3] = getRefPoints({
        google,
        heading: parsedHeading,
        polygonPoints,
    });

    const baseMeters = google.maps.geometry.spherical.computeDistanceBetween(
        point1,
        point2,
    );

    const heightMeters = google.maps.geometry.spherical.computeDistanceBetween(
        point2,
        point3,
    );

    const minAWidth = Math.min(a, heightMeters);
    const minTwoAWidth = Math.min(twoA, heightMeters);
    const minTwoAHeight = Math.min(twoA, baseMeters);
    const minFourAHeight = Math.min(fourA, baseMeters);

    const poly1Coords = buildZone({
        google,
        heading: parsedHeading,
        height: minTwoAHeight,
        start: point2,
        width: minTwoAWidth,
    });

    const poly2Start = computeOffsetPoint({
        distance: baseMeters - minTwoAHeight,
        google,
        heading: parsedHeading - 90,
        start: point2,
    });
    const poly2Coords = buildZone({
        google,
        heading: parsedHeading,
        height: minTwoAHeight,
        start: poly2Start,
        width: minTwoAWidth,
    });

    const poly3Start = computeOffsetPoint({
        distance: heightMeters - minTwoAWidth,
        google,
        heading: parsedHeading - 180,
        start: point2,
    });
    const poly3Coords = buildZone({
        google,
        heading: parsedHeading,
        height: minFourAHeight,
        start: poly3Start,
        width: minTwoAWidth,
    });

    const poly4Width = baseMeters - minFourAHeight;
    const poly4Start = computeOffsetPoint({
        distance: poly4Width,
        google,
        heading: parsedHeading - 90,
        start: computeOffsetPoint({
            distance: heightMeters - minTwoAWidth,
            google,
            heading: parsedHeading - 180,
            start: point2,
        }),
    });

    const poly4Coords = buildZone({
        google,
        heading: parsedHeading,
        height: minFourAHeight,
        start: poly4Start,
        width: minTwoAWidth,
    });

    const poly5Width = baseMeters - minFourAHeight;
    const poly5Start = computeOffsetPoint({
        distance: twoA,
        google,
        heading: parsedHeading - 90,
        start: point2,
    });
    const poly5Coords = buildZone({
        google,
        heading: parsedHeading,
        height: poly5Width > 0 ? poly5Width : 0,
        start: poly5Start,
        width: poly5Width > 0 ? minAWidth : 0,
    });

    const poly6Width = baseMeters - 2 * minFourAHeight;
    const poly6Start = computeOffsetPoint({
        distance: minFourAHeight,
        google,
        heading: parsedHeading - 90,
        start: computeOffsetPoint({
            distance: heightMeters - minTwoAWidth,
            google,
            heading: parsedHeading - 180,
            start: point2,
        }),
    });

    const poly6Coords = buildZone({
        google,
        heading: parsedHeading,
        height: poly6Width > 0 ? poly6Width : 0,
        start: poly6Start,
        width: poly6Width > 0 ? minTwoAWidth : 0,
    });

    const poly7Height = heightMeters - 2 * minTwoAWidth;
    const poly7Start = computeOffsetPoint({
        distance: minTwoAWidth,
        google,
        heading: parsedHeading - 180,
        start: point2,
    });
    const poly7Coords = buildZone({
        google,
        heading: parsedHeading,
        height: poly7Height > 0 ? minTwoAHeight : 0,
        start: poly7Start,
        width: poly7Height > 0 ? poly7Height : 0,
    });

    const poly8Height = heightMeters - 2 * twoA;
    const poly8Start = computeOffsetPoint({
        distance: minTwoAWidth,
        google,
        heading: parsedHeading - 180,
        start: computeOffsetPoint({
            start: point2,
            distance: baseMeters - minTwoAHeight,
            heading: parsedHeading - 90,
            google,
        }),
    });
    const poly8Coords = buildZone({
        google,
        heading: parsedHeading,
        height: poly8Height > 0 ? minTwoAHeight : 0,
        start: poly8Start,
        width: poly8Height > 0 ? poly8Height : 0,
    });

    const polygons = [
        { ...cornerConfig, paths: poly1Coords },
        { ...cornerConfig, paths: poly2Coords },
        { ...cornerConfig, paths: poly3Coords },
        { ...cornerConfig, paths: poly4Coords },
        { ...edgeConfig, paths: poly5Coords },
        { ...edgeConfig, paths: poly6Coords },
        { ...edgeConfig, paths: poly7Coords },
        { ...edgeConfig, paths: poly8Coords },
    ];

    return polygons;
}

function buildPitchedRoofAreasWithATiltLesserThan27Degrees({
    a,
    baseMeters,
    google,
    heading,
    heightMeters,

    point2,
}) {
    const poly1Coords = buildZone({
        google,
        heading,
        height: a,
        start: computeOffsetPoint({
            distance: -1 * (heightMeters / 2 - a),

            google,
            heading,
            start: point2,
        }),
        width: 2 * a,
    });

    const poly2Start = computeOffsetPoint({
        distance: baseMeters - a,
        google,
        heading: heading - 90,
        start: computeOffsetPoint({
            distance: -1 * (heightMeters / 2 - a),
            google,
            heading: heading,
            start: point2,
        }),
    });
    const poly2Coords = buildZone({
        google,
        heading,
        height: a,
        start: poly2Start,
        width: 2 * a,
    });

    const poly3Start = computeOffsetPoint({
        distance: baseMeters - a,
        google,
        heading: heading - 90,
        start: point2,
    });
    const poly3Coords = buildZone({
        google,
        heading,
        height: a,
        start: poly3Start,
        width: heightMeters / 2 - a,
    });

    const poly4Coords = buildZone({
        google,
        heading,
        height: a,
        start: point2,
        width: heightMeters / 2 - a,
    });

    const poly5Start = computeOffsetPoint({
        distance: baseMeters - a,
        google,
        heading: heading - 90,
        start: computeOffsetPoint({
            distance: -1 * (heightMeters / 2 + a),
            google,
            heading,
            start: point2,
        }),
    });
    const poly5Coords = buildZone({
        google,
        heading,
        height: a,

        start: poly5Start,
        width: heightMeters / 2 - a,
    });

    const poly6Start = computeOffsetPoint({
        distance: -1 * a,
        google,
        heading,
        start: computeOffsetPoint({
            distance: (-1 * heightMeters) / 2,
            google,
            heading,
            start: point2,
        }),
    });
    const poly6Coords = buildZone({
        google,
        heading,
        height: a,
        start: poly6Start,
        width: heightMeters / 2 - a,
    });

    const poly7Coords = buildZone({
        google,
        heading,
        height: baseMeters - 2 * a,
        start: computeOffsetPoint({
            distance: -1 * (heightMeters / 2 - a),
            google,
            heading,
            start: computeOffsetPoint({
                distance: -a,
                google,
                heading: heading + 90,
                start: point2,
            }),
        }),
        width: 2 * a,
    });

    const polygons = [
        { ...cornerConfig, paths: poly1Coords },
        { ...cornerConfig, paths: poly2Coords },
        { ...edgeConfig, paths: poly3Coords },
        { ...edgeConfig, paths: poly4Coords },
        { ...edgeConfig, paths: poly5Coords },
        { ...edgeConfig, paths: poly6Coords },
        { ...edgeConfig, paths: poly7Coords },
    ];

    return polygons;
}

function buildPitchedRoofAreasWithATiltGreaterThan27Degrees({
    a,
    baseMeters,
    google,
    heading,
    heightMeters,
    point2,
}) {
    const parsedHeading = parseFloat(heading);
    const poly1Coords = buildZone({
        google,
        heading: parsedHeading,
        height: a,
        start: point2,
        width: a,
    });

    const poly2Start = computeOffsetPoint({
        distance: baseMeters - a,
        google,
        heading: parsedHeading - 90,
        start: point2,
    });
    const poly2Coords = buildZone({
        google,
        heading: parsedHeading,
        height: a,
        start: poly2Start,
        width: a,
    });

    const poly3Start = computeOffsetPoint({
        distance: heightMeters - a,
        google,
        heading: parsedHeading - 180,
        start: point2,
    });
    const poly3Coords = buildZone({
        google,
        heading: parsedHeading,
        height: a,
        start: poly3Start,
        width: a,
    });

    const poly4Width = baseMeters - a;
    const poly4Start = computeOffsetPoint({
        distance: poly4Width,
        google,
        heading: parsedHeading - 90,
        start: computeOffsetPoint({
            distance: heightMeters - a,
            google,
            heading: parsedHeading - 180,
            start: point2,
        }),
    });
    const poly4Coords = buildZone({
        google,
        heading: parsedHeading,
        height: a,
        start: poly4Start,
        width: a,
    });

    const poly5Height = heightMeters - 2 * a;
    const poly5Start = computeOffsetPoint({
        distance: a,
        google,
        heading: parsedHeading - 180,
        start: point2,
    });
    const poly5Coords = buildZone({
        google,
        heading: parsedHeading,
        height: a,
        start: poly5Start,
        width: poly5Height,
    });

    const poly6Height = heightMeters - 2 * a;
    const poly6Start = computeOffsetPoint({
        distance: a,
        google,
        heading: parsedHeading - 180,
        start: computeOffsetPoint({
            distance: baseMeters - a,
            google,
            heading: parsedHeading - 90,
            start: point2,
        }),
    });
    const poly6Coords = buildZone({
        google,
        heading: parsedHeading,
        height: a,
        start: poly6Start,
        width: poly6Height,
    });

    const polygons = [
        { ...cornerConfig, paths: poly1Coords },
        { ...cornerConfig, paths: poly2Coords },
        { ...cornerConfig, paths: poly3Coords },
        { ...cornerConfig, paths: poly4Coords },
        { ...edgeConfig, paths: poly5Coords },
        { ...edgeConfig, paths: poly6Coords },
    ];

    return polygons;
}

export function buildPitchedRoofAreas({
    buildingHeight,
    ceilTilt,
    google,
    heading,
    polygonPoints,
} = {}) {
    const a = 0.4 * buildingHeight;
    const parsedHeading = parseFloat(heading);

    const [point1, point2, point3] = getRefPoints({
        google,
        heading: parsedHeading,
        polygonPoints,
    });

    const baseMeters = google.maps.geometry.spherical.computeDistanceBetween(
        point1,
        point2,
    );

    const heightMeters = google.maps.geometry.spherical.computeDistanceBetween(
        point2,
        point3,
    );

    if (ceilTilt <= PITCHED_ROOF_TILT_THRESHOLD) {
        return buildPitchedRoofAreasWithATiltLesserThan27Degrees({
            a,
            baseMeters,
            google,
            heading: parsedHeading,
            heightMeters,
            point2,
        });
    } else {
        return buildPitchedRoofAreasWithATiltGreaterThan27Degrees({
            a,
            baseMeters,
            google,
            heading: parsedHeading,
            heightMeters,
            point2,
        });
    }
}

export function computeOffsetPoint({ distance, google, heading, start } = {}) {
    const parsedDistance = parseFloat(distance);
    const parsedHeading = parseFloat(heading);
    const newPoint = google.maps.geometry.spherical.computeOffset(
        start,
        parsedDistance,
        parsedHeading,
    );
    return newPoint;
}

const getPointsViaHeading = ({ google, heading, polygonPoints } = {}) => {
    const [point1, point2] = getRefPoints({
        google,
        heading: parseFloat(heading),
        polygonPoints,
    });

    const headingNumber = parseFloat(heading);
    const parallelHeading =
        headingNumber - 180 < 0 ? headingNumber + 180 : headingNumber - 180;

    const [point3, point4] = getRefPoints({
        google,
        heading: parallelHeading,
        polygonPoints,
    });

    const point5 = google.maps.geometry.spherical.interpolate(
        point1,
        point4,
        0.5,
    );

    const point6 = google.maps.geometry.spherical.interpolate(
        point2,
        point3,
        0.5,
    );

    return { point1, point2, point3, point4, point5, point6 };
};

export const dividePolygon = ({ google, heading, polygonPoints } = {}) => {
    const { point1, point2, point3, point4, point5, point6 } =
        getPointsViaHeading({ google, heading, polygonPoints });

    const subPolygons = [
        [point1, point5, point6, point2],
        [point3, point4, point5, point6],
    ];

    return subPolygons;
};

export const getCentralLine = ({ google, heading, polygonPoints } = {}) => {
    const { point5, point6 } = getPointsViaHeading({
        google,
        heading: parseFloat(heading),
        polygonPoints,
    });

    return [point5, point6];
};

export function drawZones({ google, map, zones } = {}) {
    zones.forEach(({ paths, fillColor, strokeColor }) => {
        const polygon = new google.maps.Polygon({
            fillColor,
            fillOpacity: 0.35,
            paths,
            strokeColor,
            strokeOpacity: 0.8,
            strokeWeight: 2,
        });
        polygon.setMap(map);
    });
}

const getModuleZone = ({ zones, paths }) => {
    let ceilZone = 'center';
    for (let i = 0; i < zones.length; i++) {
        const zone = zones[i];
        const zonePolygon = turfPolygon([zone.paths]);
        const modulePolygon = turfPolygon([paths]);
        const union = turfUnion(zonePolygon, modulePolygon);
        if (union && union.geometry.coordinates.length === 1) {
            if (zone.ceilZone === 'corner') return zone.ceilZone;
            ceilZone = zone.ceilZone;
        }
    }
    return ceilZone;
};

export function getRefPoints({ google, polygonPoints, heading } = {}) {
    const parsedHeading = parseFloat(heading);

    let normalizedPolygonPoints = [...polygonPoints];

    let points = normalizedPolygonPoints.map((point) => [
        point.lng(),
        point.lat(),
    ]);
    points.push(points[0]);

    const clockwiseRing = lineString(points.map((point) => point));
    const isClockwise = booleanClockwise(clockwiseRing);

    if (!isClockwise) {
        const newClockwiseRing = rewind(clockwiseRing, { reverse: false });
        normalizedPolygonPoints = newClockwiseRing.geometry.coordinates.map(
            (point) => {
                return new google.maps.LatLng(point[1], point[0]);
            },
        );
        normalizedPolygonPoints.pop();
    }

    for (let i = 0; i < normalizedPolygonPoints.length; i++) {
        let currentHeading =
            google.maps.geometry.spherical.computeHeading(
                normalizedPolygonPoints[i],
                normalizedPolygonPoints[
                    (i + 1) % normalizedPolygonPoints.length
                ],
            ) - 90;

        if (parsedHeading === 0) currentHeading = 0;
        if (currentHeading < 0) currentHeading += 360;

        if (Math.abs(currentHeading - parsedHeading) < 0.1) {
            const point1 = normalizedPolygonPoints[i];
            const point2 =
                normalizedPolygonPoints[
                    (i + 1) % normalizedPolygonPoints.length
                ];
            const point3 =
                normalizedPolygonPoints[
                    (i + 2) % normalizedPolygonPoints.length
                ];

            return [point1, point2, point3];
        }
    }
}

export const getPolygonHeadings = ({ google, polygonCoords }) => {
    if (!google || !google.maps) return [];
    if (!polygonCoords || polygonCoords.length < 2) return [];

    const headings = [];
    for (let i = 0; i < polygonCoords.length; i++) {
        const currentPoint = new google.maps.LatLng(
            polygonCoords[i][0],
            polygonCoords[i][1],
        );
        const nextPoint = new google.maps.LatLng(
            polygonCoords[(i + 1) % polygonCoords.length][0],
            polygonCoords[(i + 1) % polygonCoords.length][1],
        );

        const heading = google.maps.geometry.spherical.computeHeading(
            currentPoint,
            nextPoint,
        );

        headings.push({
            heading: ((heading + 360) % 360).toFixed(2),
            points: [currentPoint, nextPoint],
        });
    }

    return headings.sort(
        (a, b) => parseFloat(a.heading) - parseFloat(b.heading),
    );
};

export const setCeilZone = ({
    buildingHeight,
    ceilTilt,
    google,
    groups,
    heading,
    polygon,
    sloofType,
}) => {
    if (!google || !google.maps) return groups;

    let newGroups = Array(groups.length).fill(null);

    const polygonPoints = polygon.map(
        (latlng) => new google.maps.LatLng({ lat: latlng[0], lng: latlng[1] }),
    );

    let zones = [];

    if (sloofType === 'double_slope')
        zones = buildPitchedRoofAreas({
            buildingHeight,
            ceilTilt,
            google,
            heading,
            polygonPoints,
        });
    else
        zones = buildMonoPitchedRoofAreas({
            buildingHeight,
            google,
            heading,
            polygonPoints,
        });

    const preparedZones = zones.map((zone) => {
        const newPaths = zone.paths.map((point) => [point.lng(), point.lat()]);
        newPaths.push(newPaths[0]);
        return { ...zone, paths: newPaths };
    });

    for (let index = 0; index < groups.length; index++) {
        const group = groups[index];
        const zoned = group.map((row) =>
            row.map((col) => {
                const modulePaths = col.paths.map((point) => [
                    point[1],
                    point[0],
                ]);
                modulePaths.push(modulePaths[0]);
                const ceilZone = getModuleZone({
                    zones: preparedZones,
                    paths: modulePaths,
                });
                return { ...col, ceil_zone: ceilZone };
            }),
        );
        newGroups[index] = zoned;
    }

    return newGroups;
};
